type APIResponse = {
  url: string;
  status: number;
  statusText: string;
};

/**
 * Error class for API errors that includes response details
 */
export class APIError<TResponse extends APIResponse = APIResponse> extends Error {
  method: string;
  response: TResponse;
  toJSON: () => unknown;
  skipRetry: boolean;

  constructor(message: string, method: string, response: TResponse, responseText: string) {
    super(message);
    this.method = method;
    this.name = "APIError";
    this.response = response;

    // skip retry on client-side errors, since there is no point in trying to recover those
    this.skipRetry = response.status >= 400 && response.status < 500 && response.status !== 409;

    this.toJSON = function toJSON() {
      // return an object with the error details because JSON.stringify only serializes
      // own properties, and we are inheriting from Error, so most of the error details are not owned
      return {
        message: this.message,
        stack: this.stack,
        response: {
          method,
          url: this.response.url,
          status: this.response.status,
          statusText: this.response.statusText,
          responseText,
        },
      };
    };
  }

  static instanceof(error: unknown): error is APIError {
    return error instanceof Error && error.name === "APIError";
  }
}

/**
 * Custom error class so that we can use JSON.stringify() and still get error details
 */
export class AppError extends Error {
  context: Record<string, unknown>;
  toJSON: () => unknown;

  constructor(message: string, context?: Record<string, unknown>) {
    super(message);
    this.name = "AppError";
    this.context = context ?? {};
    this.toJSON = function toJSON() {
      // return an object with the error details because JSON.stringify only serializes
      // own properties, and we are inheriting from Error, so most of the error details are not owned
      return {
        message: this.message,
        stack: this.stack,
        context: this.context,
      };
    };
  }

  static instanceof(error: unknown): error is AppError {
    return error instanceof Error && error.name === "AppError";
  }
}
